import { z } from 'zod';

import { IdSchema, NonEmptyStringSchema } from '../../base';
import { FunctionCallAssertionSchema } from './testing.schema';

const uniqueTags = (tags: string[]) => new Set(tags).size === tags.length;

export const CreateTestCaseSchema = z
  .object({
    id: IdSchema,
    name: NonEmptyStringSchema,
    scenario: NonEmptyStringSchema,
    expectedOutcome: z.string().default(''),
    variantId: IdSchema.nullable().default(null),
    language: z.string().default(''),
    simulatedAt: z.string().nullable().default(null),
  })
  .describe('Command to create a new test case for agent testing');

export type CreateTestCasePayload = z.infer<typeof CreateTestCaseSchema>;

export const UpdateTestCaseSchema = z
  .object({
    id: IdSchema,
    name: NonEmptyStringSchema.optional(),
    scenario: NonEmptyStringSchema.optional(),
    expectedOutcome: z.string().optional(),
    variantId: IdSchema.nullable().optional(),
    language: z.string().optional(),
    simulatedAt: z.string().nullable().optional(),
  })
  .refine(
    ({ id, ...rest }) => Object.values(rest).some(v => v !== undefined),
    'At least one field besides id must be provided',
  )
  .describe("Command to update an existing test case's properties");

export type UpdateTestCasePayload = z.infer<typeof UpdateTestCaseSchema>;

export const DeleteTestCaseSchema = z
  .object({
    id: IdSchema,
  })
  .describe('Command to delete a test case by its ID');

export type DeleteTestCasePayload = z.infer<typeof DeleteTestCaseSchema>;

export const SetTestCaseAssertionsSchema = z
  .object({
    id: IdSchema,
    assertions: z.array(z.string()),
  })
  .describe('Command to set all text assertions on a test case');

export type SetTestCaseAssertionsPayload = z.infer<
  typeof SetTestCaseAssertionsSchema
>;

export const SetTestCaseFunctionCallsSchema = z
  .object({
    id: IdSchema,
    functionCalls: z.array(FunctionCallAssertionSchema),
  })
  .describe('Command to set all function call assertions on a test case');

export type SetTestCaseFunctionCallsPayload = z.infer<
  typeof SetTestCaseFunctionCallsSchema
>;

export const SetTestCaseTagsSchema = z
  .object({
    id: IdSchema,
    tags: z.array(z.string()).refine(uniqueTags, 'Tags must be unique'),
  })
  .describe('Command to set tags on a test case');

export type SetTestCaseTagsPayload = z.infer<typeof SetTestCaseTagsSchema>;
